import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiMail, FiShield, FiFileText } = FiIcons;

const LegalModal = ({ isOpen, onClose, type }) => {
  const content = {
    privacy: {
      title: "Privacy Policy",
      icon: FiShield,
      body: (
        <div className="space-y-6 text-text-secondary leading-relaxed">
          <p>Last Updated: {new Date().toLocaleDateString()}</p>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">1. Information We Collect</h4>
            <p>We collect information you provide directly to us when you make a purchase, subscribe to our newsletter, or contact us for support. This may include your name, email address, and payment information processed securely via SamCart.</p>
          </section>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">2. How We Use Your Information</h4>
            <p>We use the information we collect to deliver the digital products you purchase, send transactional emails, provide customer support, and send occasional marketing updates if you have opted in.</p>
          </section>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">3. Data Security</h4>
            <p>We implement a variety of security measures to maintain the safety of your personal information. Your sensitive information is encrypted and transmitted via Secure Socket Layer (SSL) technology.</p>
          </section>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">4. Third-Party Disclosure</h4>
            <p>We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties except for trusted third parties who assist us in operating our website and conducting our business (like SamCart for payments).</p>
          </section>
        </div>
      )
    },
    terms: {
      title: "Terms of Service",
      icon: FiFileText,
      body: (
        <div className="space-y-6 text-text-secondary leading-relaxed">
          <p>Last Updated: {new Date().toLocaleDateString()}</p>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">1. Digital Product License</h4>
            <p>Upon purchase, we grant you a personal, non-exclusive, non-transferable license to access and use "Simple Online Income For Retirees" for your personal, non-commercial use only.</p>
          </section>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">2. Refund Policy</h4>
            <p>We offer a 30-day money-back guarantee. If you are not satisfied with the product, please contact us at support@digitaldownloads.info within 30 days of purchase for a full refund.</p>
          </section>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">3. Earnings Disclaimer</h4>
            <p>Any earnings or income statements are only estimates of what we think you could earn. There is no assurance you will do as well as stated in any examples. Your success depends on your background, dedication, and effort.</p>
          </section>
          <section>
            <h4 className="text-brand-dark font-bold mb-2">4. Limitation of Liability</h4>
            <p>In no event shall Simple Online Income be liable for any damages arising out of the use or inability to use the materials on this website.</p>
          </section>
        </div>
      )
    },
    contact: {
      title: "Contact Support",
      icon: FiMail,
      body: (
        <div className="text-center py-8 space-y-8">
          <div className="w-20 h-20 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto text-brand-gold">
            <SafeIcon icon={FiMail} className="text-4xl" />
          </div>
          <div className="space-y-4">
            <p className="text-xl text-text-primary">Have a question or need help with your order?</p>
            <p className="text-text-secondary">Our support team is here to help you. Please reach out to us via email and we'll get back to you within 24-48 hours.</p>
          </div>
          <a 
            href="mailto:support@digitaldownloads.info" 
            className="inline-block px-8 py-4 bg-brand-dark text-white font-bold rounded-full hover:bg-slate-800 transition-colors text-lg"
          >
            support@digitaldownloads.info
          </a>
        </div>
      )
    }
  };

  const activeContent = content[type] || content.privacy;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-brand-dark/60 backdrop-blur-sm"
          />
          
          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-brand-gold/10 rounded-xl flex items-center justify-center text-brand-gold">
                  <SafeIcon icon={activeContent.icon} className="text-xl" />
                </div>
                <h3 className="text-2xl font-display font-black text-brand-dark">{activeContent.title}</h3>
              </div>
              <button 
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-brand-dark transition-colors"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-8 overflow-y-auto custom-scrollbar">
              {activeContent.body}
            </div>

            {/* Footer */}
            <div className="p-6 bg-bg-alt border-t border-slate-100 text-center flex-shrink-0">
              <button 
                onClick={onClose}
                className="px-8 py-2 font-bold text-slate-500 hover:text-brand-dark transition-colors"
              >
                Close Window
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LegalModal;